<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import type { Severity } from '../types/severity';
import type { Size } from '../types/size';
import { NuxtLink } from '#components';
import { Tooltip } from 'floating-vue';
import Icon from './Icon.vue';

interface Props {
	label?: string;
	icon?: string;
	severity?: Severity;
	size?: Extract<Size, 'sm' | 'md'>;
	to?: RouteLocationRaw;
	external?: boolean;
	target?: '_blank' | '_self' | '_parent' | '_top';
	isActive?: boolean;
	disabled?: boolean;
	info?: string;
	loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	severity: 'basic',
	size: 'md',
});
</script>

<template>
	<component
		:class="props.info ? undefined : 'dropdown-item-wrapper'"
		:placement="props.info ? 'right-center' : undefined"
		:is="props.info ? Tooltip : 'div'"
	>
		<component
			class="dropdown-item"
			:class="{
				[`dropdown-item--${props.severity}`]: props.severity,
				[`dropdown-item--${props.size}`]: props.size,
				'dropdown-item--is-active': props.isActive,
				'dropdown-item--disabled': props.disabled,
			}"
			:to="props.to"
			:type="props.to ? undefined : 'button'"
			:external="props.external"
			:target="props.target"
			:disabled="props.disabled"
			:is="props.to ? NuxtLink : 'button'"
		>
			<span class="dropdown-item__content">
				<span class="dropdown-item__icon" v-if="props.icon || props.loading">
					<HmSpinner size="xs" v-if="props.loading" />
					<Icon :name="props.icon" v-else-if="props.icon" />
				</span>
				<span v-if="props.label">{{ props.label }}</span>
				<slot v-else-if="$slots.default" />
			</span>
			<span class="dropdown-item__info" v-if="props.info">
				<Icon name="info" />
			</span>
		</component>

		<template #popper>
			{{ props.info }}
		</template>
	</component>
</template>

<style scoped lang="scss">
@layer components
{
	.dropdown-item-wrapper
	{
		@include flex;
	}
	.dropdown-item
	{
		flex-grow:1;
		@include flex(null, center, 16);
		transition:var(--transition-default);

		&__content
		{
			flex-grow:1;
			@include flex(null, center, 8);
			line-height:1;
			font-weight:500;
		}
		&__icon
		{
			@include flex;
		}
		&__info
		{
			@include flex;

			&:deep(.icon)
			{
				@include svg(16);
			}
		}

		&--basic
		{
			color:var(--text-basic-800);

			&:not(.dropdown-item--disabled)
			{
				@include hover
				{
					color:var(--text-basic-950);
					background-color:var(--bg-basic-200);
				}
			}
		}
		&--success
		{
			color:var(--other-success);

			&:not(.dropdown-item--disabled)
			{
				@include hover
				{
					background-color:color-opacity(var(--other-success), 8);
				}
			}
		}
		&--warning
		{
			color:var(--other-warning);

			&:not(.dropdown-item--disabled)
			{
				@include hover
				{
					background-color:color-opacity(var(--other-warning), 8);
				}
			}
		}
		&--error
		{
			color:var(--other-error);

			&:not(.dropdown-item--disabled)
			{
				@include hover
				{
					background-color:color-opacity(var(--other-error), 8);
				}
			}
		}
		&--info
		{
			color:var(--other-info);

			&:not(.dropdown-item--disabled)
			{
				@include hover
				{
					background-color:color-opacity(var(--other-info), 8);
				}
			}
		}

		&--sm
		{
			padding:$spacing-xs $spacing-md;

			&:has(.dropdown-item__icon)
			{
				padding-block:calc($spacing-2xs + 2px);
			}

			.dropdown-item__content
			{
				font-size:1.2rem;

				&:deep(.icon)
				{
					@include svg(16);
				}
			}
		}
		&--md
		{
			padding:$spacing-sm $spacing-md;

			&:has(.dropdown-item__icon)
			{
				padding-block:calc($spacing-xs + 1px);
			}

			.dropdown-item__content
			{
				font-size:1.4rem;

				&:deep(.icon)
				{
					@include svg(20);
				}
			}
		}

		&--is-active
		{
			color:var(--text-basic-950);
			background-color:var(--bg-basic-300);

			&:not(.dropdown-item--disabled)
			{
				@include hover
				{
					background-color:var(--bg-basic-400);
				}
			}
		}
		&--disabled
		{
			cursor:not-allowed;

			.dropdown-item__content
			{
				opacity:0.36;
			}
		}
	}
}
</style>
